// The Pads screen's editing, as one thing a page can hold.
//
// The timeline's clip operations (useClipOps) brought to the Session View:
// edit, duplicate, copy / cut / paste, delete, rename, and "put it on the
// timeline", plus Live's scene commands. Everything decidable is in
// lib/pad-ops.ts and lib/edits.ts; this holds the selection, the pad
// clipboard, a line of text saying what happened, and the call to `propose`.
//
// A create is fire-and-forget in a connected room — the new clip's id is the
// room's to mint — so an operation that should leave its result selected (or
// open in the clip editor) says WHERE it put it, and the pad is picked up
// when the room shows a clip there.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CanonicalClipState, CanonicalRoom } from "@shared/sync/state.js";
import type { Intent } from "@shared/sync/intents.js";
import {
  copyClipToLane,
  copyClipToSlot,
  createMidiSlotClip,
  deleteClips,
  deleteScene,
  duplicateScene,
  insertScene,
  mintTempId,
  renameScene,
  setClipFields,
} from "@web-shared/lib/edits";
import { firstFreeSlot, snap } from "@web-shared/lib/placement";
import {
  duplicateRow,
  ghostSource,
  keepRow,
  padClip,
  reconcileSelection,
  sameSelection,
  slotAccepts,
  slotTaken,
  type PadSelection,
} from "@/lib/pad-ops";
import { cleanTrackName } from "@/lib/track-name";

const NOTE_MS = 4000;
/** A new MIDI pad's length: four bars of 4/4. */
const NEW_PAD_BEATS = 16;

export interface PadOpsArgs {
  room: CanonicalRoom | null;
  editor: { propose: (intents: Intent[]) => void };
  /** The Set's scene count without the ghost rows. */
  sceneCount: number;
  /** Beats a placement on the timeline lands on. */
  grid: number;
  /** Where "to the timeline" puts a pad: the insert marker, read when asked. */
  insertBeats: () => number;
  /** Open a clip in the clip editor. */
  onOpen: (clipId: string) => void;
}

export interface PadOps {
  selection: PadSelection | null;
  select: (sel: PadSelection | null) => void;
  /** The pad the selection names, resolved against the room. */
  clip: CanonicalClipState | null;
  /** The selection is a timeline (ghost) pad. */
  ghost: boolean;
  clipboard: CanonicalClipState | null;
  can: {
    edit: boolean;
    duplicate: boolean;
    copy: boolean;
    cut: boolean;
    paste: boolean;
    remove: boolean;
    rename: boolean;
    toTimeline: boolean;
    /** Insert / duplicate a scene: one of the Set's own rows is selected. */
    scene: boolean;
  };
  edit: () => void;
  /** A held pad, slot or scene: selected, and a pad of the Set's own opened
   * in its editor (Koala's and Note's hold-to-edit). A timeline pad is only
   * selected — opening one would keep a copy of it first. */
  hold: (sel: PadSelection) => void;
  /** A timeline pad, kept as a scene clip. */
  keep: () => void;
  duplicate: () => void;
  copy: () => void;
  cut: () => void;
  paste: () => void;
  remove: () => void;
  rename: (name: string) => void;
  toTimeline: () => void;
  /** Every timeline pad in a row, kept as one new scene. */
  keepRow: (cells: ReadonlyArray<{ clipId: string; laneId: string }>) => void;
  insertScene: () => void;
  duplicateScene: () => void;
  note: string | null;
}

/** One gesture, one undo: a multi-intent operation lands or dies together. */
function asGesture(intents: Intent[]): Intent[] {
  if (intents.length <= 1) return intents;
  const gestureId = mintTempId("gesture");
  return intents.map((i) => ({ ...i, gestureId }));
}

export function usePadOps({ room, editor, sceneCount, grid, insertBeats, onOpen }: PadOpsArgs): PadOps {
  const [rawSelection, setSelection] = useState<PadSelection | null>(null);
  const [clipboard, setClipboard] = useState<{ clip: CanonicalClipState; brace: boolean } | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const noteTimer = useRef<number | undefined>(undefined);
  /** A pad an operation just created, to select (and maybe open) once the
   * room shows it. */
  const [pending, setPending] = useState<{ laneId: string; row: number; open: boolean } | null>(null);

  const say = useCallback((message: string) => {
    setNote(message);
    window.clearTimeout(noteTimer.current);
    noteTimer.current = window.setTimeout(() => setNote(null), NOTE_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(noteTimer.current), []);

  const selection = useMemo(() => reconcileSelection(room, rawSelection), [room, rawSelection]);
  const select = useCallback((sel: PadSelection | null) => {
    // The same value keeps the same object, so nothing memoised on it moves.
    setSelection((prev) => (sameSelection(prev, sel) ? prev : sel));
  }, []);

  // Pick up what an operation created, once the room has it.
  useEffect(() => {
    if (!pending || !room) return;
    const made = room.clips.find((c) => c.trackId === pending.laneId && c.slot === pending.row);
    if (!made) return;
    setPending(null);
    setSelection({ kind: "pad", clipId: made.id, laneId: pending.laneId, row: pending.row });
    if (pending.open) onOpen(made.id);
  }, [room, pending, onOpen]);

  const padId = selection?.kind === "pad" ? selection.clipId : null;
  const clip = useMemo(() => (padId ? padClip(room, padId) : null), [room, padId]);
  const ghost = padId !== null && ghostSource(padId) !== null;
  const carries = clip !== null && (clip.clipKind === "midi" || clip.audioKey !== null);

  const propose = useCallback((intents: Intent[]): boolean => {
    if (intents.length === 0) return false;
    editor.propose(asGesture(intents));
    return true;
  }, [editor]);

  const laneName = useCallback(
    (laneId: string) => cleanTrackName(room?.tracks.find((t) => t.id === laneId)?.name ?? "") || "this track",
    [room],
  );

  /** Copy the selected pad into `row` on its lane; select (and open) the copy. */
  const copyInto = useCallback(
    (row: number | null, open: boolean, doneNote: string) => {
      if (!room || !clip || selection?.kind !== "pad") return;
      if (row === null) {
        say(`${laneName(selection.laneId)} has no empty slot left.`);
        return;
      }
      const intent = copyClipToSlot(room, clip.id, selection.laneId, row, { braceWarped: ghost });
      if (!intent) {
        say("That pad has nothing to copy.");
        return;
      }
      propose([intent]);
      setPending({ laneId: selection.laneId, row, open });
      say(doneNote.replace("{row}", String(row + 1)));
    },
    [room, clip, selection, ghost, propose, say, laneName],
  );

  const keep = useCallback(() => {
    if (!room || !ghost || selection?.kind !== "pad") return;
    copyInto(keepRow(room, selection.laneId, sceneCount), false, "Kept as a scene clip, in scene {row}.");
  }, [room, ghost, selection, sceneCount, copyInto]);

  const edit = useCallback(() => {
    if (!room) return;
    // An empty slot on an instrument track: a new clip there, opened.
    if (selection?.kind === "slot") {
      const intent = createMidiSlotClip(room, selection.laneId, selection.row, NEW_PAD_BEATS);
      if (!intent) return;
      propose([intent]);
      setPending({ laneId: selection.laneId, row: selection.row, open: true });
      return;
    }
    if (!clip || selection?.kind !== "pad") return;
    if (!ghost) {
      onOpen(clip.id);
      return;
    }
    // A timeline pad is kept before it is edited: the arrangement's own
    // placements stay exactly as they were.
    copyInto(keepRow(room, selection.laneId, sceneCount), true, "Kept as a scene clip in scene {row}, so the timeline stays as it was.");
  }, [room, clip, selection, ghost, onOpen, copyInto, sceneCount, propose]);

  // A held pad opens — a timeline pad as the timeline clip behind it
  // (`padClip` resolves a ghost to its source), with no copy made first.
  // Keep / Duplicate on the action bar are there for a copy on purpose.
  const hold = useCallback((sel: PadSelection) => {
    select(sel);
    if (sel.kind !== "pad") return;
    const held = padClip(room, sel.clipId);
    if (held) onOpen(held.id);
  }, [room, select, onOpen]);

  const duplicate = useCallback(() => {
    if (!room || selection?.kind !== "pad") return;
    if (ghost) {
      keep();
      return;
    }
    copyInto(duplicateRow(room, selection.laneId, selection.row), false, "Duplicated into scene {row}.");
  }, [room, selection, ghost, keep, copyInto]);

  const copy = useCallback(() => {
    if (!clip || !carries) return;
    setClipboard({ clip, brace: ghost });
    say(`Copied ${clip.name || "the pad"}. Pick an empty slot and paste.`);
  }, [clip, carries, ghost, say]);

  const remove = useCallback(() => {
    if (!room || !selection) return;
    if (selection.kind === "scene") {
      if (selection.row >= sceneCount) return;
      const hasClips = room.clips.some((c) => c.slot === selection.row);
      if (propose(deleteScene(room, selection.row, sceneCount))) {
        say(hasClips ? `Deleted scene ${selection.row + 1} and its clips.` : `Deleted scene ${selection.row + 1}.`);
        setSelection(null);
      }
      return;
    }
    if (selection.kind !== "pad" || ghost || !clip) return;
    if (propose(deleteClips(room, [clip.id]))) {
      say(`Deleted ${clip.name || "the pad"}.`);
      setSelection({ kind: "slot", laneId: selection.laneId, row: selection.row });
    }
  }, [room, selection, ghost, clip, propose, say, sceneCount]);

  const cut = useCallback(() => {
    if (!room || !clip || ghost || !carries || selection?.kind !== "pad") return;
    setClipboard({ clip, brace: false });
    if (propose(deleteClips(room, [clip.id]))) {
      say(`Cut ${clip.name || "the pad"}. Pick an empty slot and paste.`);
      setSelection({ kind: "slot", laneId: selection.laneId, row: selection.row });
    }
  }, [room, clip, ghost, carries, selection, propose, say]);

  /** Where a paste would land: the selected empty slot, or the first empty
   * slot below the selected pad on its lane. */
  const pasteTarget = useMemo((): { laneId: string; row: number } | null => {
    if (!room || !clipboard || !selection || selection.kind === "scene") return null;
    if (selection.kind === "slot") return { laneId: selection.laneId, row: selection.row };
    const row = duplicateRow(room, selection.laneId, selection.row);
    return row === null ? null : { laneId: selection.laneId, row };
  }, [room, clipboard, selection]);
  const canPaste = room !== null && clipboard !== null && pasteTarget !== null
    && slotAccepts(room, clipboard.clip, pasteTarget.laneId, pasteTarget.row);

  const paste = useCallback(() => {
    if (!room || !clipboard || !pasteTarget) return;
    if (!slotAccepts(room, clipboard.clip, pasteTarget.laneId, pasteTarget.row)) {
      say(slotTaken(room, pasteTarget.laneId, pasteTarget.row)
        ? "That slot already has a clip."
        : `A ${clipboard.clip.clipKind === "midi" ? "MIDI" : "audio"} clip can't go on ${laneName(pasteTarget.laneId)}.`);
      return;
    }
    // The clipboard is a record, not a live id: a cut clip is gone from the
    // room, so the copy is built from the record itself.
    // Off every lane, so it can never count as occupying the target slot.
    const { slot: _slot, ...record } = clipboard.clip;
    const data: CanonicalClipState = { ...record, id: "__clipboard__", trackId: "__clipboard__" };
    const shadow: CanonicalRoom = { ...room, clips: [...room.clips, data] };
    const intent = copyClipToSlot(shadow, data.id, pasteTarget.laneId, pasteTarget.row, { braceWarped: clipboard.brace });
    if (!intent) {
      say("Nothing on the clipboard can be pasted there.");
      return;
    }
    propose([intent]);
    setPending({ ...pasteTarget, open: false });
    say(`Pasted into scene ${pasteTarget.row + 1}.`);
  }, [room, clipboard, pasteTarget, propose, say, laneName]);

  const rename = useCallback(
    (name: string) => {
      if (!room || !selection) return;
      const trimmed = name.trim();
      if (selection.kind === "scene") {
        if (selection.row >= sceneCount) return;
        const intent = renameScene(room, selection.row, trimmed);
        if (intent) propose([intent]);
        return;
      }
      if (selection.kind !== "pad" || ghost || !clip || !trimmed || trimmed === clip.name) return;
      const intent = setClipFields(room, clip.id, { name: trimmed });
      if (intent) propose([intent]);
    },
    [room, selection, ghost, clip, propose],
  );

  const toTimeline = useCallback(() => {
    if (!room || !clip || !carries || selection?.kind !== "pad") return;
    const at = Math.max(0, snap(insertBeats(), grid));
    const intent = copyClipToLane(room, clip.id, selection.laneId, at, (start, duration) =>
      firstFreeSlot(room, selection.laneId, start, duration),
    );
    if (!intent || intent.kind !== "upsertClip") {
      say(`No room on ${laneName(selection.laneId)}'s timeline there.`);
      return;
    }
    propose([intent]);
    const bar = Math.floor(intent.clip.startTime / 4) + 1;
    say(`Placed on ${laneName(selection.laneId)}'s timeline at bar ${bar}.`);
  }, [room, clip, carries, selection, insertBeats, grid, propose, say, laneName]);

  const keepGhostRow = useCallback(
    (cells: ReadonlyArray<{ clipId: string; laneId: string }>) => {
      if (!room) return;
      // One new row past the Set's scenes that no lane uses yet.
      const used = new Set(room.clips.filter((c) => c.slot !== undefined).map((c) => c.slot));
      let row = Math.max(0, sceneCount);
      while (used.has(row)) row++;
      const intents = cells
        .map(({ clipId, laneId }) => {
          const source = ghostSource(clipId) ?? clipId;
          return copyClipToSlot(room, source, laneId, row, { braceWarped: true });
        })
        .filter((i): i is Intent => i !== null);
      if (propose(intents)) say(`Kept the row as scene ${row + 1}.`);
    },
    [room, sceneCount, propose, say],
  );

  const sceneRow = selection?.kind === "scene" ? selection.row : null;
  const insert = useCallback(() => {
    if (!room || sceneRow === null || sceneRow >= sceneCount) return;
    if (propose(insertScene(room, sceneRow, sceneCount))) say(`Inserted an empty scene at ${sceneRow + 1}.`);
  }, [room, sceneRow, sceneCount, propose, say]);
  const duplicateSceneRow = useCallback(() => {
    if (!room || sceneRow === null || sceneRow >= sceneCount) return;
    if (propose(duplicateScene(room, sceneRow, sceneCount))) {
      say(`Duplicated scene ${sceneRow + 1}.`);
      setSelection({ kind: "scene", row: sceneRow + 1 });
    }
  }, [room, sceneRow, sceneCount, propose, say]);

  const isPad = selection?.kind === "pad" && clip !== null;
  const emptyMidiSlot =
    selection?.kind === "slot" &&
    room?.tracks.find((t) => t.id === selection.laneId)?.trackKind === "midi" &&
    !room.clips.some((c) => c.trackId === selection.laneId && c.slot === selection.row);
  // A scene command only means something on one of the Set's own rows: the
  // timeline's rows past them are drawn, not stored.
  const realScene = selection?.kind === "scene" && selection.row < sceneCount;
  const clipboardClip = clipboard?.clip ?? null;
  // Memoised: the room page re-renders every frame the transport moves, and
  // the pads must not re-render with it.
  return useMemo(() => ({
    selection,
    select,
    clip,
    ghost,
    clipboard: clipboardClip,
    can: {
      edit: (isPad && carries) || emptyMidiSlot,
      duplicate: isPad && carries,
      copy: isPad && carries,
      cut: isPad && carries && !ghost,
      paste: canPaste,
      remove: (isPad && !ghost) || realScene,
      rename: (isPad && !ghost) || realScene,
      toTimeline: isPad && carries,
      scene: realScene,
    },
    edit,
    hold,
    keep,
    duplicate,
    copy,
    cut,
    paste,
    remove,
    rename,
    toTimeline,
    keepRow: keepGhostRow,
    insertScene: insert,
    duplicateScene: duplicateSceneRow,
    note,
  }), [selection, select, clip, ghost, clipboardClip, isPad, emptyMidiSlot, carries, canPaste, realScene, edit, hold, keep,
    duplicate, copy, cut, paste, remove, rename, toTimeline, keepGhostRow, insert, duplicateSceneRow, note]);
}
